import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  HStack,
  VStack,
  Text,
  Collapse,
  useDisclosure,
  InputGroup,
  InputLeftElement,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

export default function CreditNoteFilters({ onSearch, isLoading }) {
  const { isOpen, onToggle } = useDisclosure();
  const [filters, setFilters] = useState({
    customerOrDocument: '',
    invoiceNumber: '',
    startDate: '',
    endDate: '',
  });

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    // Separar el campo combinado en cliente y documento para el backend
    const searchFilters = {
      customer: filters.customerOrDocument,
      document: filters.customerOrDocument,
      invoiceNumber: filters.invoiceNumber,
      startDate: filters.startDate,
      endDate: filters.endDate,
    };

    // Solo enviar filtros que tengan valor
    const activeFilters = Object.entries(searchFilters).reduce((acc, [key, value]) => {
      if (value && value.trim() !== '') {
        acc[key] = value.trim();
      }
      return acc;
    }, {});

    onSearch(activeFilters);
  };

  const handleClear = () => {
    setFilters({
      customerOrDocument: '',
      invoiceNumber: '',
      startDate: '',
      endDate: '',
    });
    onSearch({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value && value.trim() !== '');

  return (
    <Box
      bg={cardBg}
      borderRadius="20px"
      border="1px solid"
      borderColor={borderColor}
      p="20px"
      mb="20px"
      boxShadow="0px 2px 5.5px rgba(0, 0, 0, 0.02)"
    >
      <VStack spacing={4} align="stretch">
        {/* Búsqueda principal */}
        <HStack spacing={4}>
          <FormControl flex="1">
            <InputGroup>
              <InputLeftElement>
                <Icon as={SearchIcon} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por cliente o documento (RUT/CI)..."
                value={filters.customerOrDocument}
                onChange={(e) => handleFilterChange('customerOrDocument', e.target.value)}
                bg="white"
                _placeholder={{ color: 'gray.500' }}
              />
            </InputGroup>
          </FormControl>
          
          <Button
            colorScheme="gray"
            onClick={handleSearch}
            isLoading={isLoading}
            loadingText="Buscando..."
            minW="120px"
          >
            Aplicar filtros
          </Button>
          
          <Button
            variant="outline"
            onClick={onToggle}
            rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            minW="140px"
          >
            Filtros Avanzados
          </Button>
        </HStack>

        {/* Filtros avanzados colapsables */}
        <Collapse in={isOpen} animateOpacity>
          <VStack spacing={4} pt={4} align="stretch">
            <Text fontSize="sm" fontWeight="600" color="gray.600">
              Filtros Avanzados
            </Text>
            
            <HStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Número de Factura Referencia</FormLabel>
                <Input
                  placeholder="Ej: 123, A123, etc."
                  value={filters.invoiceNumber}
                  onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
                  bg="white"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel fontSize="sm">Fecha Desde</FormLabel>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  bg="white"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel fontSize="sm">Fecha Hasta</FormLabel>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  bg="white"
                />
              </FormControl>
            </HStack>
            
            <HStack spacing={4}>
              <Button
                colorScheme="gray"
                onClick={handleSearch}
                isLoading={isLoading}
                loadingText="Aplicando filtros..."
                size="sm"
              >
                Aplicar Filtros
              </Button>
              
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={handleClear}
                  size="sm"
                >
                  Limpiar Filtros
                </Button>
              )}
            </HStack>
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}